import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Award, Users, DollarSign, TrendingUp, ExternalLink, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import RecommendationsPanel from "@/components/dashboard/RecommendationsPanel";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

/**
 * Dashboard → Referrals tab.
 *
 * Shows every business that signed up via this user's listing CTA strip
 * ("Run a business in {city}? Claim your free listing"). Once a referred
 * business converts to a paid plan, the original owner becomes eligible for
 * affiliate commission (Stripe Connect payout — to be wired in a later phase).
 */
export default function DashboardReferrals() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const origin = (typeof window !== "undefined" && window.location.origin) || "https://localbest.ai";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_V1}/affiliate/my-referrals`, { headers: getAuthHeader() })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  const copyRefLink = async (slug) => {
    const url = `https://localbest.ai/submit?ref=${encodeURIComponent(slug)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(slug);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(null), 1800);
    } catch (_) { toast.error("Copy failed"); }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="referrals-loading">
        <div className="h-24 bg-white/5 rounded-xl animate-pulse" />
        <div className="h-48 bg-white/5 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16" data-testid="referrals-empty">
          <Users className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
          <p className="text-white font-medium">No referral data yet</p>
          <p className="text-[#94A3B8] text-sm mt-1">Once your listing is approved, share its CTA link and your referrals will appear here.</p>
        </div>
        <RecommendationsPanel />
      </div>
    );
  }

  const { my_listings = [], referrals = [], total_referred = 0, paid_referred = 0 } = data;

  return (
    <div className="space-y-6" data-testid="dashboard-referrals">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EC4899] flex items-center justify-center flex-shrink-0">
          <Award className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white font-['Outfit']">My Referrals</h1>
          <p className="text-[#94A3B8] text-sm mt-1 max-w-2xl">
            Every business that claims a free listing through your listing's CTA shows up here.
            When a referred business upgrades to a paid plan, you become eligible for affiliate commission.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="referrals-stats">
        <Stat icon={Users} label="Businesses Referred" value={total_referred} color="#00E5FF" />
        <Stat icon={DollarSign} label="Paid Conversions" value={paid_referred} color="#10B981" />
        <Stat icon={TrendingUp} label="Conversion Rate" value={total_referred > 0 ? `${Math.round((paid_referred / total_referred) * 100)}%` : "—"} color="#F59E0B" />
      </div>

      {/* My listings — copyable referral links */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 p-5" data-testid="my-referral-links">
        <h2 className="text-white font-semibold mb-1">Your Referral Links</h2>
        <p className="text-xs text-[#94A3B8] mb-4">Share these per-listing links anywhere off-platform. The CTA on each public listing page also uses them automatically.</p>
        {my_listings.length === 0 ? (
          <p className="text-[#64748B] text-sm">No listings yet.</p>
        ) : (
          <ul className="space-y-2">
            {my_listings.map((l) => {
              const url = `${origin}/submit?ref=${encodeURIComponent(l.slug)}`;
              return (
                <li key={l.id || l.slug} className="flex items-center gap-3 rounded-lg bg-white/5 border border-white/5 px-3 py-2">
                  <span className="text-white font-medium text-sm truncate flex-1">{l.name}</span>
                  <span className="text-[#FBBF24] text-xs font-mono">{l.referral_count || 0} ref{(l.referral_count || 0) === 1 ? "" : "s"}</span>
                  <code className="hidden md:inline-block text-xs text-[#E2E8F0] bg-black/40 rounded px-2 py-1 truncate max-w-[280px]">{url}</code>
                  <button
                    onClick={() => copyRefLink(l.slug)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-white/10 bg-white/5 hover:bg-white/10 text-white"
                    data-testid={`copy-ref-${l.slug}`}
                    title="Copy referral link"
                  >
                    {copied === l.slug ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Referrals table */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10 p-5" data-testid="referrals-table-card">
        <h2 className="text-white font-semibold mb-3">Referred Businesses</h2>
        {referrals.length === 0 ? (
          <p className="text-[#94A3B8] text-sm">No referrals yet — share your CTA link to start earning credits.</p>
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#64748B] border-b border-white/5">
                  <th className="py-2 pr-3">Business</th>
                  <th className="py-2 pr-3">City</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3">Referred</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r) => {
                  const isPaid = !!(r.is_paid || (r.plan && r.plan !== "free") || r.stripe_subscription_id);
                  return (
                    <tr key={r.id || r.slug} className="border-b border-white/5 last:border-0" data-testid={`referral-row-${r.slug}`}>
                      <td className="py-2 pr-3 text-white font-medium">{r.name}</td>
                      <td className="py-2 pr-3 text-[#94A3B8]">{[r.city, r.state].filter(Boolean).join(", ") || "—"}</td>
                      <td className="py-2 pr-3 text-[#94A3B8]">{r.category_name || "—"}</td>
                      <td className="py-2 pr-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] bg-white/5 text-[#94A3B8] border border-white/10">
                          {r.claim_status || r.status || r.submission_status || "pending"}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] border ${isPaid ? "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30" : "bg-white/5 text-[#94A3B8] border-white/10"}`}>
                          {isPaid ? "Paid" : "Free"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-[#64748B] text-xs whitespace-nowrap">
                        {r.referred_at ? new Date(r.referred_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pr-3">
                        <a href={`/business/${r.slug}`} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] hover:text-[#22D3EE] inline-flex items-center gap-1">
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recommendations / Trusted Network */}
      <RecommendationsPanel />

      {/* Future-state callout */}
      <div className="rounded-lg bg-gradient-to-r from-[#10B981]/10 to-[#7C3AED]/10 border border-[#10B981]/20 p-4 text-sm text-[#94A3B8]" data-testid="payout-callout">
        <strong className="text-white">Coming soon:</strong> Connect a Stripe account to automatically receive affiliate payouts when a referred business upgrades to a paid plan.
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}22`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</p>
        <p className="text-white font-bold text-lg leading-tight">{value}</p>
      </div>
    </div>
  );
}
